/**
 * Capability readiness — the wire shape served by the API, plus the pure
 * presentation rules the System Health panel renders with.
 *
 * WHAT THIS IS. The API runs a boot-time readiness contract (see
 * `packages/api/src/services/system/capability-readiness/`) that asks, for each
 * capability the app advertises, "could this actually succeed right now?" from
 * config and the database only. It exists because three real defects shared one
 * shape: the app could not possibly perform an advertised capability, nothing
 * had asked it to yet, so nothing failed and nobody knew.
 *
 * The rules below live in their own module — separate from the JSX — because
 * the web test runner is configured with `environment: "node"` and cannot render
 * components. Keeping ordering and status mapping pure is what makes them
 * testable at all, and the ordering is load-bearing: a critical not-ready
 * capability has to be the first row an admin sees.
 */

export type ReadinessStatus = "ready" | "not-ready" | "unknown";
export type ReadinessSeverity = "critical" | "warning" | "info";

export interface CapabilityRecord {
  id: string;
  key: string;
  label: string;
  status: ReadinessStatus;
  severity: ReadinessSeverity;
  /** What IS true right now — one sentence. */
  detail: string;
  /** The fix, naming the exact field or env var. */
  remediation: string;
  requires: string[];
}

export interface CapabilityReadinessSummary {
  total: number;
  ready: number;
  notReady: number;
  unknown: number;
  /** not-ready OR unknown at critical severity — the "act now" count. */
  criticalUnready: number;
}

export interface CapabilityReadiness {
  generatedAt?: string;
  capabilities?: CapabilityRecord[];
  summary?: CapabilityReadinessSummary;
}

/** MUI Chip colors, kept narrow so the panel cannot drift off the palette. */
export type ChipColor = "success" | "error" | "warning" | "info" | "default";

export const STATUS_META: Record<
  ReadinessStatus,
  { label: string; color: ChipColor }
> = {
  ready: { label: "Ready", color: "success" },
  // Never green, never grey: an unreadable check is a finding, not a pass.
  unknown: { label: "Unknown", color: "warning" },
  "not-ready": { label: "Not ready", color: "error" },
};

export const SEVERITY_META: Record<
  ReadinessSeverity,
  { label: string; color: ChipColor }
> = {
  critical: { label: "Critical", color: "error" },
  warning: { label: "Warning", color: "warning" },
  info: { label: "Info", color: "default" },
};

/** Anything that is not `ready` needs an operator's eyes. */
export const isUnready = (record: CapabilityRecord): boolean =>
  record.status !== "ready";

const SEVERITY_RANK: Record<ReadinessSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

const STATUS_RANK: Record<ReadinessStatus, number> = {
  "not-ready": 0,
  unknown: 1,
  ready: 2,
};

/**
 * Worst-first: unready before ready, critical before info, then alphabetical so
 * the order is stable across polls. Returns a new array — never sorts in place,
 * because the caller's array comes straight off the fetched snapshot.
 */
export const sortCapabilities = (
  records: CapabilityRecord[],
): CapabilityRecord[] =>
  [...records].sort(
    (a, b) =>
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
      a.label.localeCompare(b.label),
  );

/**
 * The counts to show. Prefers the server's own rollup; recomputes from the rows
 * when it is absent so an older API (or a partial payload) still renders
 * something truthful rather than zeros.
 */
export const summarizeCapabilities = (
  readiness: CapabilityReadiness | null | undefined,
): CapabilityReadinessSummary => {
  const records = readiness?.capabilities ?? [];
  if (readiness?.summary) return readiness.summary;
  return {
    total: records.length,
    ready: records.filter((r) => r.status === "ready").length,
    notReady: records.filter((r) => r.status === "not-ready").length,
    unknown: records.filter((r) => r.status === "unknown").length,
    criticalUnready: records.filter(
      (r) => r.severity === "critical" && r.status !== "ready",
    ).length,
  };
};

/** One-line banner text for the panel header. */
export const readinessHeadline = (
  summary: CapabilityReadinessSummary,
): string => {
  const unready = summary.notReady + summary.unknown;
  if (summary.total === 0) return "No capabilities are registered.";
  if (unready === 0)
    return `All ${summary.total} capabilities are ready.`;
  const criticals = summary.criticalUnready
    ? ` — ${summary.criticalUnready} critical`
    : "";
  return `${unready} of ${summary.total} capabilities are not ready${criticals}.`;
};
